/**
 * Live board updates over the app's own WebSocket channel (PLAN.md §5.1).
 *
 * The socket carries *signals*, not state: a message says "ticket X changed", and React Query
 * refetches. Nothing here writes into the cache directly. That keeps the server the single
 * source of truth — a client that reconstructed rows from event payloads would slowly drift out
 * of sync with permission filtering, computed roll-ups and anything else the API decides.
 *
 * Event types are slug-prefixed by the backend (`servicedesk.ticket_created`). We match on the
 * suffix so the app keeps working if the deployment's slug is changed.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { getToken } from '@/services/api';

/** Ceiling on the reconnect backoff. Long enough not to hammer a restarting API. */
const MAX_BACKOFF_MS = 30_000;
const BASE_BACKOFF_MS = 1_000;

interface ServerMessage {
  type: string;
  data?: Record<string, unknown>;
}

function websocketUrl(token: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/v1/ws?token=${encodeURIComponent(token)}`;
}

/** Strip the `<slug>.` namespace so handlers match on the bare event name. */
function bareEvent(type: string): string {
  const dot = type.indexOf('.');
  return dot === -1 ? type : type.slice(dot + 1);
}

/**
 * Subscribe to the app's realtime channel for the lifetime of the mounting component.
 *
 * Mount this once, high in the tree (AppLayout). Mounting it twice opens two sockets.
 */
export function useRealtime(): void {
  const client = useQueryClient();
  const socketRef = useRef<WebSocket | null>(null);
  const attemptRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  // Set on unmount so an in-flight reconnect timer doesn't resurrect the socket afterwards.
  const closedRef = useRef(false);

  useEffect(() => {
    closedRef.current = false;

    const connect = (): void => {
      const token = getToken();
      // Not signed in yet: don't open an unauthenticated socket the server would only reject.
      // The effect re-runs on login because AppLayout remounts behind ProtectedRoute.
      if (!token || closedRef.current) return;

      const socket = new WebSocket(websocketUrl(token));
      socketRef.current = socket;

      socket.onopen = () => {
        attemptRef.current = 0;
      };

      socket.onmessage = (event: MessageEvent<string>) => {
        let message: ServerMessage;
        try {
          message = JSON.parse(event.data) as ServerMessage;
        } catch {
          return;
        }
        handle(message);
      };

      socket.onclose = () => {
        socketRef.current = null;
        if (closedRef.current) return;
        // Exponential backoff. A dropped socket is normal (laptop lid, wifi, API redeploy), so
        // it retries quietly rather than surfacing an error to the user — the UI still works
        // without realtime, it just refreshes on its own polling schedule.
        const delay = Math.min(BASE_BACKOFF_MS * 2 ** attemptRef.current, MAX_BACKOFF_MS);
        attemptRef.current += 1;
        timerRef.current = window.setTimeout(connect, delay);
      };
    };

    const handle = (message: ServerMessage): void => {
      const event = bareEvent(message.type);
      const ticketId = (message.data?.id ?? message.data?.ticket_id) as string | undefined;

      switch (event) {
        case 'ticket_created':
        case 'ticket_updated':
          void client.invalidateQueries({ queryKey: ['tickets'] });
          if (ticketId) void client.invalidateQueries({ queryKey: ['ticket', ticketId] });
          break;
        case 'ticket_note_added':
          if (ticketId) void client.invalidateQueries({ queryKey: ['ticket', ticketId] });
          break;
        default:
          // `ping` and any event type this client version doesn't know about. Ignoring unknown
          // types (rather than warning) is what lets the backend ship a new event before the
          // frontend that consumes it.
          break;
      }
    };

    connect();

    return () => {
      closedRef.current = true;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [client]);
}
